import { Dispatch, SetStateAction } from "react";
import { Center, Flex } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";

import { Alert, BodyShort } from "@digital-services-gard-as/anchor-react";
import { ClaimParams } from "@/api/claimsApi";
import { isApiError } from "@/utils/errorUtils";
import BranchCompanyFilter from "./branchCompany";
import ClosedClaimsSwitch from "./ClosedClaimsSwitch";
import ClaimsSearch from "./ClaimsSearch";

const STATUS_200_SUCCESS = 200;
const STATUS_504_GATEWAY_TIMEOUT = 504;

type ClaimsSearchProps = {
    isClaimsSearchError: boolean;
    isFetchingClaims: boolean;
    claimsQuery: ClaimParams;
    setClaimsQuery: Dispatch<SetStateAction<ClaimParams>>;
    resetPagination: () => void;
    claimsSearchError?: unknown;
};

const ClaimsTableToolbar = ({
    isClaimsSearchError,
    isFetchingClaims,
    resetPagination,
    setClaimsQuery,
    claimsQuery,
    claimsSearchError
}: ClaimsSearchProps) => {
    const { t } = useTranslation("claimsInvoicesOverview");

    if (isClaimsSearchError) {
        let errorMessage;
        if (
            isUnprocessableStatus(claimsSearchError) &&
            (claimsSearchError as ApiErrorWithStatus).originalStatus === STATUS_504_GATEWAY_TIMEOUT
        ) {
            errorMessage = t("errorMessages:claimsInvoicesOverview.cannotRetrieveDataWithTimeOutError");
        } else {
            errorMessage = t("errorMessages:claimsInvoicesOverview.cannotRetrieveData");
        }

        return (
            <Center>
                <Alert variant="danger" id="claimsNoDataAlert" data-testid="claimsNoDataAlert">
                    <BodyShort>{errorMessage}</BodyShort>
                </Alert>
            </Center>
        );
    }

    return (
        <Flex w="100%">
            <BranchCompanyFilter setClaimsQuery={setClaimsQuery} />
            <ClaimsSearch
                claimsQuery={claimsQuery}
                isFetchingClaims={isFetchingClaims}
                resetPagination={resetPagination}
                setClaimsQuery={setClaimsQuery}
            />
            <ClosedClaimsSwitch resetPagination={resetPagination} setClaimsQuery={setClaimsQuery} />
        </Flex>
    );
};

interface ApiErrorWithStatus {
    originalStatus?: number;
}

const isUnprocessableStatus = (error: unknown) => {
    if (!error) {
        return false;
    }
    if (isApiError(error)) {
        // You can adjust this logic to match your API's error structure
        return (
            (error as ApiErrorWithStatus)?.originalStatus !== STATUS_200_SUCCESS || error.status !== STATUS_200_SUCCESS
        );
    }
    return false;
};

export default ClaimsTableToolbar;
